import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, PackageOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeService } from "@/lib/calc";
import { SectionCard, SectionTitle, InfoBox } from "@/components/ui-kit";
import { GuidedTip } from "@/components/GuidedTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const inputClass = "bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-3 focus-visible:ring-2 focus-visible:ring-brand-champagne";
const CATEGORIES = ["gel", "base", "color", "top", "prep", "files", "disposable", "cleaning", "nailart", "other"];

function emptyLib() {
  return { name: "", category: "other", package_price: "", package_quantity: "", unit: "ml", notes: "" };
}

export default function MaterialsPage() {
  const { t, user, fmt } = useAuth();
  const qc = useQueryClient();
  const { data: materials = [] } = useQuery({ queryKey: ["materials"], queryFn: () => api.get("/materials").then((r) => r.data) });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });

  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null); // { id?, ...fields }
  const [toDelete, setToDelete] = useState(null);
  const [impact, setImpact] = useState(null); // { form, affected: [{name, before, after}] }

  const filtered = useMemo(() => materials.filter((m) => {
    const okCat = catFilter === "all" || m.category === catFilter;
    const okSearch = !search || (m.name || "").toLowerCase().includes(search.toLowerCase());
    return okCat && okSearch;
  }), [materials, search, catFilter]);

  const affectedServices = (materialId) => services.filter((s) => (s.materials || []).some((m) => m.material_id === materialId));

  const openNew = () => setEditing(emptyLib());
  const openEdit = (m) => setEditing({ id: m.id, name: m.name, category: m.category || "other", package_price: m.package_price, package_quantity: m.package_quantity, unit: m.unit, notes: m.notes || "" });

  const payloadFrom = (f) => ({
    name: (f.name || "").trim(), category: f.category || "",
    package_price: Number(f.package_price) || 0, package_quantity: Number(f.package_quantity) || 1,
    unit: f.unit, notes: f.notes || "",
  });

  const doSave = async () => {
    const f = editing;
    if (!f.name.trim()) { toast.error(t("materials.name") + " — " + t("common.required")); return; }
    const payload = payloadFrom(f);
    if (f.id) {
      // Compute impact on linked services
      const affected = affectedServices(f.id);
      const priceChanged = Number(f.package_price) !== 0 || Number(f.package_quantity) !== 0;
      if (affected.length > 0 && priceChanged) {
        const rows = affected.map((s) => {
          const before = computeService(s, user);
          const simService = { ...s, materials: (s.materials || []).map((m) => m.material_id === f.id ? { ...m, package_price: payload.package_price, package_quantity: payload.package_quantity, unit: payload.unit, method: "detailed" } : m) };
          const after = computeService(simService, user);
          return { name: s.name, beforeMat: before.totalMaterial, afterMat: after.totalMaterial, beforeRec: before.commercial.nice, afterRec: after.commercial.nice };
        });
        setImpact({ payload, id: f.id, affected: rows });
        return;
      }
    }
    await persist(f.id, payload);
  };

  const persist = async (id, payload) => {
    try {
      if (id) await api.put(`/materials/${id}`, payload);
      else await api.post("/materials", payload);
      qc.invalidateQueries({ queryKey: ["materials"] });
      qc.invalidateQueries({ queryKey: ["services"] });
      setEditing(null); setImpact(null);
      toast.success(t("materials.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const confirmDelete = async () => {
    await api.delete(`/materials/${toDelete.id}`);
    qc.invalidateQueries({ queryKey: ["materials"] });
    qc.invalidateQueries({ queryKey: ["services"] });
    setToDelete(null);
    toast.success(t("materials.deleted"));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("materials.title")}</h1>
          <p className="mt-1 text-brand-taupe">{t("materials.subtitle")}</p>
        </div>
        <Button onClick={openNew} data-testid="materials-add-button" className="rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 h-12 px-5">
          <Plus className="mr-2 h-5 w-5" /> <span className="hidden sm:inline">{t("materials.add")}</span>
        </Button>
      </div>

      <GuidedTip id="materials-library">{t("tips.materialsLibrary")}</GuidedTip>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-taupe" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("materials.search")} className={`${inputClass} pl-9`} data-testid="materials-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className={`${inputClass} sm:w-56`} data-testid="materials-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">{t("materials.allCategories")}</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`materials.cat_${c}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <SectionCard accent className="text-center py-14">
          <PackageOpen className="mx-auto h-10 w-10 text-brand-taupe" />
          <h3 className="mt-3 font-serif text-2xl text-brand-anthracite">{t("materials.empty")}</h3>
          <p className="mt-2 text-brand-taupe max-w-sm mx-auto">{t("materials.emptyDesc")}</p>
          <Button onClick={openNew} data-testid="materials-empty-cta" className="mt-6 rounded-xl bg-brand-anthracite text-brand-ivory">{t("materials.add")}</Button>
        </SectionCard>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {filtered.map((m) => {
            const unitCost = (Number(m.package_quantity) || 0) > 0 ? Number(m.package_price) / Number(m.package_quantity) : 0;
            const used = affectedServices(m.id).length;
            return (
              <SectionCard key={m.id} className="!p-5" data-testid={`material-lib-card-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-serif text-lg font-medium text-brand-anthracite">{m.name}</h3>
                    <span className="mt-1 inline-flex rounded-full bg-brand-blush/40 border border-brand-taupe/20 px-2.5 py-0.5 text-xs">{t(`materials.cat_${m.category || "other"}`)}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(m)} data-testid={`material-lib-edit-${m.id}`} className="rounded-lg p-2 text-brand-taupe hover:bg-brand-sand hover:text-brand-anthracite"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setToDelete({ id: m.id, name: m.name, used })} data-testid={`material-lib-delete-${m.id}`} className="rounded-lg p-2 text-brand-taupe hover:bg-[#B85B5B]/10 hover:text-[#B85B5B]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-3 border-t border-brand-line pt-3 text-sm text-brand-taupe">
                  {fmt(m.package_price)} / {m.package_quantity} {t(`editor.unit_${["ml","g","pieces"].includes(m.unit) ? m.unit : "custom"}`)} · <span className="text-brand-anthracite font-medium">{fmt(unitCost)}/{m.unit}</span>
                  {used > 0 && <div className="mt-1 text-xs text-brand-champagne">{t("materials.usedInN", { n: used })}</div>}
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white border-brand-line max-w-md max-h-[90vh] overflow-y-auto" data-testid="material-lib-dialog">
          <DialogHeader><DialogTitle className="font-serif">{editing?.id ? t("materials.edit") : t("materials.newTitle")}</DialogTitle>
            <DialogDescription className="text-brand-taupe">{t("materials.subtitle")}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>{t("materials.name")}</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputClass} data-testid="material-lib-name" /></div>
              <div className="space-y-2"><Label>{t("materials.category")}</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger className={inputClass} data-testid="material-lib-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`materials.cat_${c}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label className="text-xs">{t("materials.packagePrice")}</Label>
                  <Input type="number" min="0" value={editing.package_price} onChange={(e) => setEditing({ ...editing, package_price: e.target.value })} className={inputClass} data-testid="material-lib-price" /></div>
                <div className="space-y-2"><Label className="text-xs">{t("materials.packageQty")}</Label>
                  <Input type="number" min="0" value={editing.package_quantity} onChange={(e) => setEditing({ ...editing, package_quantity: e.target.value })} className={inputClass} data-testid="material-lib-qty" /></div>
              </div>
              <GuidedTip id="package-quantity">{t("tips.packageQuantity")}</GuidedTip>
              <div className="space-y-2"><Label className="text-xs">{t("materials.unit")}</Label>
                <Select value={editing.unit} onValueChange={(v) => setEditing({ ...editing, unit: v })}>
                  <SelectTrigger className={inputClass} data-testid="material-lib-unit"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["ml", "g", "pieces", "custom"].map((u) => <SelectItem key={u} value={u}>{t(`editor.unit_${u}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label className="text-xs">{t("materials.notes")}</Label>
                <Textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder={t("materials.notesPlaceholder")} className={inputClass} data-testid="material-lib-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl border-brand-line">{t("common.cancel")}</Button>
            <Button onClick={doSave} data-testid="material-lib-save" className="rounded-xl bg-brand-anthracite text-brand-ivory">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impact preview dialog */}
      <Dialog open={!!impact} onOpenChange={(o) => !o && setImpact(null)}>
        <DialogContent className="bg-white border-brand-line max-w-md" data-testid="material-impact-dialog">
          <DialogHeader><DialogTitle className="font-serif">{t("materials.updateImpactTitle")}</DialogTitle>
            <DialogDescription className="text-brand-taupe">{impact ? t("materials.usedInN", { n: impact.affected.length }) : ""}</DialogDescription>
          </DialogHeader>
          {impact && (
            <div className="space-y-3">
              <p className="text-sm text-brand-taupe">{t("materials.usedInN", { n: impact.affected.length })}</p>
              <div className="space-y-2">
                {impact.affected.map((r, i) => (
                  <div key={i} className="rounded-xl border border-brand-line p-3 text-sm" data-testid={`impact-row-${i}`}>
                    <div className="font-medium text-brand-anthracite">{r.name}</div>
                    <div className="mt-1 flex justify-between text-brand-taupe"><span>{t("materials.materialCost")}</span><span className="tabular-nums">{fmt(r.beforeMat)} → <span className="text-brand-anthracite font-medium">{fmt(r.afterMat)}</span></span></div>
                    <div className="flex justify-between text-brand-taupe"><span>{t("materials.recommended")}</span><span className="tabular-nums">{fmt(r.beforeRec)} → <span className="text-brand-anthracite font-medium">{fmt(r.afterRec)}</span></span></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpact(null)} className="rounded-xl border-brand-line">{t("common.cancel")}</Button>
            <Button onClick={() => persist(impact.id, impact.payload)} data-testid="material-impact-confirm" className="rounded-xl bg-brand-anthracite text-brand-ivory">{t("materials.confirmUpdate")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white border-brand-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">{t("materials.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.used > 0 ? t("materials.deleteWarnUsed", { n: toDelete.used }) : t("materials.deleteWarnNone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="material-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="material-delete-confirm" className="bg-[#B85B5B] hover:bg-[#a24f4f]">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
